import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Divider, FormControl,
    FormHelperText, FormLabel, Input, Option, Select, Sheet, Stack,
    Tab, TabList, TabPanel, Table, Tabs, Typography, tabClasses,
} from '@mui/joy';
import { useSearchParams } from 'react-router';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

import AddCardRoundedIcon from '@mui/icons-material/AddCardRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import { useAdvertiser } from '../../../AdvertiserContext';
import {
    APIAdsBillingSettings, APIAdsBillingSummary, APIAdsDepositDetail,
    APIAdsDeposits, APIAdsStatementDownload, APIAdsTransactions,
    adsErrorText, saveBlob,
} from '../../../api_ads';
import AdDepositModal from '../../../components/Ads/AdDepositModal';
import BusinessReferralCard from '../../../components/Referral/BusinessReferralCard';
import AdDepositReturnHandler from '../../../components/Ads/AdDepositReturnHandler';
import { useAdsAccount } from '../../../components/Ads/useAdsData';
import {
    DEPOSIT_STATUS_COLORS, DEPOSIT_STATUS_LABELS,
    TX_TYPE_COLORS, TX_TYPE_LABELS, formatMoney,
} from '../../../components/Ads/adsConstants';


function Metric({ label, value, hint, color = 'neutral', big }) {
    return (
        <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md', flex: 1, minWidth: 160 }}>
            <Typography level="body-xs" color="neutral">{label}</Typography>
            <Typography
                level={big ? 'h3' : 'title-lg'}
                sx={{ color: `${color}.700`, fontWeight: 700, mt: 0.3 }}
            >
                {value}
            </Typography>
            {hint && <Typography level="body-xs" color="neutral">{hint}</Typography>}
        </Sheet>
    );
}


function DepositsTable({ token, onCheck }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await APIAdsDeposits(token, { pageSize: 30 });
            setItems(res?.items || []);
        } catch (e) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const recheck = async (deposit) => {
        setBusy(deposit.id);
        try {
            await APIAdsDepositDetail(token, deposit.id);
            await load();
            onCheck?.();
        } finally {
            setBusy(null);
        }
    };

    if (loading) {
        return <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size="sm" /></Stack>;
    }

    if (!items.length) {
        return (
            <Typography level="body-sm" color="neutral" sx={{ py: 3, textAlign: 'center' }}>
                Пополнений пока не было
            </Typography>
        );
    }

    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
            <Table sx={{ '--TableCell-paddingY': '10px' }}>
                <thead>
                    <tr>
                        <th style={{ width: 70 }}>№</th>
                        <th style={{ width: 140 }}>Сумма</th>
                        <th style={{ width: 160 }}>Статус</th>
                        <th style={{ width: 170 }}>Создано</th>
                        <th style={{ width: 170 }}>Оплачено</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((deposit) => (
                        <tr key={deposit.id}>
                            <td>{deposit.id}</td>
                            <td>
                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                    {formatMoney(deposit.amount)} ₽
                                </Typography>
                                {Number(deposit.refunded_amount) > 0 && (
                                    <Typography level="body-xs" color="warning">
                                        возвращено {formatMoney(deposit.refunded_amount)} ₽
                                    </Typography>
                                )}
                            </td>
                            <td>
                                <Chip
                                    size="sm"
                                    variant="soft"
                                    color={DEPOSIT_STATUS_COLORS[deposit.status] || 'neutral'}
                                >
                                    {DEPOSIT_STATUS_LABELS[deposit.status] || deposit.status}
                                </Chip>
                            </td>
                            <td>
                                <Typography level="body-xs">
                                    {deposit.date_create
                                        ? format(new Date(deposit.date_create), 'd MMM yyyy, HH:mm', { locale: ru })
                                        : '—'}
                                </Typography>
                            </td>
                            <td>
                                <Typography level="body-xs">
                                    {deposit.paid_at
                                        ? format(new Date(deposit.paid_at), 'd MMM yyyy, HH:mm', { locale: ru })
                                        : '—'}
                                </Typography>
                            </td>
                            <td>
                                {deposit.status === 'pending' && (
                                    <Button
                                        size="sm"
                                        variant="soft"
                                        loading={busy === deposit.id}
                                        onClick={() => recheck(deposit)}
                                    >
                                        Проверить статус
                                    </Button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Sheet>
    );
}


function TransactionsTable({ token }) {
    const [items, setItems] = useState([]);
    const [pages, setPages] = useState(1);
    const [page, setPage] = useState(1);
    const [txType, setTxType] = useState('');
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await APIAdsTransactions(token, {
                txType: txType || undefined, page, pageSize: 30,
            });
            setItems(res?.items || []);
            setPages(res?.pages || 1);
        } catch (e) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [token, txType, page]);

    useEffect(() => { load(); }, [load]);

    return (
        <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <Select
                    size="sm"
                    value={txType}
                    onChange={(_, v) => { setTxType(v ?? ''); setPage(1); }}
                    sx={{ minWidth: 200 }}
                >
                    <Option value="">Все операции</Option>
                    {Object.entries(TX_TYPE_LABELS).map(([value, label]) => (
                        <Option key={value} value={value}>{label}</Option>
                    ))}
                </Select>
            </Stack>

            {loading ? (
                <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size="sm" /></Stack>
            ) : !items.length ? (
                <Typography level="body-sm" color="neutral" sx={{ py: 3, textAlign: 'center' }}>
                    Операций не найдено
                </Typography>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                    <Table sx={{ '--TableCell-paddingY': '10px' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 170 }}>Дата</th>
                                <th style={{ width: 160 }}>Операция</th>
                                <th style={{ width: 130 }}>Сумма</th>
                                <th style={{ width: 140 }}>Баланс после</th>
                                <th>Комментарий</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((tx) => (
                                <tr key={tx.id}>
                                    <td>
                                        <Typography level="body-xs">
                                            {tx.date_create
                                                ? format(new Date(tx.date_create), 'd MMM yyyy, HH:mm', { locale: ru })
                                                : '—'}
                                        </Typography>
                                    </td>
                                    <td>
                                        <Chip
                                            size="sm"
                                            variant="soft"
                                            color={TX_TYPE_COLORS[tx.tx_type] || 'neutral'}
                                        >
                                            {tx.tx_type_display || TX_TYPE_LABELS[tx.tx_type] || tx.tx_type}
                                        </Chip>
                                    </td>
                                    <td>
                                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                            {formatMoney(tx.amount)} ₽
                                        </Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-sm">
                                            {formatMoney(tx.balance_after)} ₽
                                        </Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs" color="neutral">
                                            {tx.comment || '—'}
                                        </Typography>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            {pages > 1 && (
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                    <Button size="sm" variant="outlined" color="neutral"
                            disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                        Назад
                    </Button>
                    <Typography level="body-sm" sx={{ alignSelf: 'center' }}>{page} / {pages}</Typography>
                    <Button size="sm" variant="outlined" color="neutral"
                            disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                        Вперёд
                    </Button>
                </Stack>
            )}

            <Alert color="neutral" variant="soft" size="sm" sx={{ mt: 2 }}>
                Операции «Резерв» и «Снятие резерва» не меняют баланс — это движение
                внутри уже имеющихся денег под дневные бюджеты кампаний.
                Показы за 1000 просмотров списываются партиями, поэтому строка
                «Списание» может появиться с задержкой в несколько минут.
            </Alert>
        </Box>
    );
}


export default function AdsBillingPage() {
    const { advertiserToken: token } = useAdvertiser();
    const { account, limits, loading: accountLoading, reload: reloadAccount } = useAdsAccount();
    const [searchParams, setSearchParams] = useSearchParams();

    const returnedDepositId = searchParams.get('deposit');

    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [depositOpen, setDepositOpen] = useState(false);
    const [tab, setTab] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);

    const [threshold, setThreshold] = useState('');
    const [savingThreshold, setSavingThreshold] = useState(false);

    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [downloading, setDownloading] = useState(false);

    const loadSummary = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdsBillingSummary(token, 30);
            setSummary(res);
            setThreshold(String(res?.low_balance_threshold ?? ''));
        } catch (e) {
            setError(adsErrorText(e, 'Не удалось загрузить данные по балансу'));
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { loadSummary(); }, [loadSummary, reloadKey]);

    const refreshAll = () => {
        setReloadKey((k) => k + 1);
        reloadAccount();
    };

    const saveThreshold = async () => {
        try {
            setSavingThreshold(true);
            setError(null);
            await APIAdsBillingSettings(token, Number(threshold) || 0);
            setNotice('Порог уведомления сохранён');
            await loadSummary();
        } catch (e) {
            setError(adsErrorText(e));
        } finally {
            setSavingThreshold(false);
        }
    };

    const downloadStatement = async () => {
        try {
            setDownloading(true);
            setError(null);
            const blob = await APIAdsStatementDownload(token, { dateFrom, dateTo });
            saveBlob(blob, `ad_statement_${dateFrom || 'all'}_${dateTo || 'now'}.csv`);
        } catch (e) {
            setError('Не удалось выгрузить детализацию');
        } finally {
            setDownloading(false);
        }
    };

    const daysLeft = summary?.days_left;
    const lowBalance = typeof daysLeft === 'number' && daysLeft <= 3 && daysLeft < 999;

    return (
        <Box>
            {returnedDepositId && (
                <AdDepositReturnHandler
                    depositId={returnedDepositId}
                    onSettled={() => refreshAll()}
                    onDismiss={() => {
                        const next = new URLSearchParams(searchParams);
                        next.delete('deposit');
                        setSearchParams(next, { replace: true });
                    }}
                />
            )}

            <BusinessReferralCard />

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Typography level="h4">Баланс и оплата</Typography>
                    <Typography level="body-sm" color="neutral">
                        Рекламный счёт не связан с партнёрским балансом — это разные кошельки
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        color="neutral"
                        startDecorator={<RefreshRoundedIcon />}
                        onClick={refreshAll}
                        loading={loading || accountLoading}
                    >
                        Обновить
                    </Button>
                    <Button
                        startDecorator={<AddCardRoundedIcon />}
                        onClick={() => setDepositOpen(true)}
                        disabled={account?.status !== 'active'}
                    >
                        Пополнить
                    </Button>
                </Stack>
            </Stack>

            {notice && (
                <Alert color="success" sx={{ mb: 2 }} endDecorator={
                    <Button size="sm" variant="plain" color="success" onClick={() => setNotice(null)}>
                        Скрыть
                    </Button>
                }>{notice}</Alert>
            )}
            {error && (
                <Alert color="danger" sx={{ mb: 2 }} endDecorator={
                    <Button size="sm" variant="plain" color="danger" onClick={() => setError(null)}>
                        Скрыть
                    </Button>
                }>{error}</Alert>
            )}

            {summary?.autostop_at && (
                <Alert color="danger" sx={{ mb: 2 }}>
                    <Box>
                        <Typography level="title-sm">Показы остановлены из-за нехватки средств</Typography>
                        <Typography level="body-sm">
                            Кампании возобновятся автоматически сразу после пополнения счёта.
                        </Typography>
                    </Box>
                </Alert>
            )}

            {lowBalance && !summary?.autostop_at && (
                <Alert color="warning" sx={{ mb: 2 }}>
                    <Box>
                        <Typography level="title-sm">Бюджет заканчивается</Typography>
                        <Typography level="body-sm">
                            При текущем темпе трат баланса хватит примерно на {daysLeft} дн.
                        </Typography>
                    </Box>
                </Alert>
            )}

            {loading && !summary ? (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : (
                <>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                        <Metric
                            big
                            label="Свободный остаток"
                            value={`${formatMoney(summary?.available_balance)} ₽`}
                            hint="доступно для новых кампаний"
                            color={Number(summary?.available_balance) > 0 ? 'success' : 'danger'}
                        />
                        <Metric
                            label="В резерве"
                            value={`${formatMoney(summary?.hold_balance)} ₽`}
                            hint="под дневные бюджеты"
                        />
                        <Metric
                            label="Общий баланс"
                            value={`${formatMoney(summary?.balance)} ₽`}
                        />
                        <Metric
                            label="Расход в среднем"
                            value={`${formatMoney(summary?.avg_daily_spend)} ₽`}
                            hint="в сутки за последнюю неделю"
                        />
                        <Metric
                            label="Хватит на"
                            value={daysLeft >= 999 ? '—' : `${daysLeft} дн.`}
                            hint={daysLeft >= 999 ? 'расхода пока нет' : 'при текущем темпе'}
                            color={lowBalance ? 'warning' : 'neutral'}
                        />
                    </Stack>

                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                        <Metric
                            label={`Потрачено за ${summary?.period_days || 30} дн.`}
                            value={`${formatMoney(summary?.spent_in_period)} ₽`}
                        />
                        <Metric
                            label={`Пополнено за ${summary?.period_days || 30} дн.`}
                            value={`${formatMoney(summary?.deposited_in_period)} ₽`}
                        />
                    </Stack>
                </>
            )}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ bgcolor: 'transparent' }}>
                <TabList sx={{
                    mb: 2, borderRadius: 'md',
                    [`& .${tabClasses.root}.${tabClasses.selected}`]: {
                        bgcolor: 'primary.softBg', color: 'primary.700',
                    },
                }}>
                    <Tab>Пополнения</Tab>
                    <Tab>Все операции</Tab>
                    <Tab>Настройки и выгрузка</Tab>
                </TabList>

                <TabPanel value={0} sx={{ p: 0 }}>
                    <DepositsTable key={reloadKey} token={token} onCheck={refreshAll} />
                </TabPanel>

                <TabPanel value={1} sx={{ p: 0 }}>
                    <TransactionsTable key={reloadKey} token={token} />
                </TabPanel>

                <TabPanel value={2} sx={{ p: 0 }}>
                    <Stack spacing={2}>
                        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                            <Typography level="title-sm" sx={{ mb: 1 }}>
                                Уведомление о низком балансе
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-end">
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Порог, ₽</FormLabel>
                                    <Input
                                        type="number"
                                        value={threshold}
                                        onChange={(e) => setThreshold(e.target.value)}
                                        placeholder="0"
                                    />
                                    <FormHelperText>
                                        0 — считать автоматически как трёхдневный расход
                                    </FormHelperText>
                                </FormControl>
                                <Button loading={savingThreshold} onClick={saveThreshold}>
                                    Сохранить
                                </Button>
                            </Stack>
                        </Sheet>

                        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                            <Typography level="title-sm" sx={{ mb: 1 }}>
                                Детализация операций
                            </Typography>
                            <Typography level="body-sm" color="neutral" sx={{ mb: 1.5 }}>
                                Выгрузка в CSV со всеми пополнениями, списаниями и резервами
                                за период. Открывается в Excel и в Google Таблицах.
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-end">
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>С даты</FormLabel>
                                    <Input
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.target.value)}
                                    />
                                </FormControl>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>По дату</FormLabel>
                                    <Input
                                        type="date"
                                        value={dateTo}
                                        onChange={(e) => setDateTo(e.target.value)}
                                    />
                                </FormControl>
                                <Button
                                    variant="soft"
                                    startDecorator={<DownloadRoundedIcon />}
                                    loading={downloading}
                                    onClick={downloadStatement}
                                >
                                    Скачать CSV
                                </Button>
                            </Stack>
                        </Sheet>

                        <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
                            <Typography level="title-sm" sx={{ mb: 0.5 }}>
                                Реквизиты для чека
                            </Typography>
                            <Typography level="body-sm" color="neutral">
                                Юридические данные рекламодателя и контакты для чеков
                                меняются в разделе «Данные» партнёрского кабинета.
                                Они же попадают в объявления как обязательная информация
                                о рекламодателе.
                            </Typography>
                        </Sheet>
                    </Stack>
                </TabPanel>
            </Tabs>

            <AdDepositModal
                open={depositOpen}
                account={account}
                limits={limits}
                suggestedAmount={lowBalance ? 5000 : null}
                onClose={() => setDepositOpen(false)}
            />
        </Box>
    );
}